import axiosInstance from '../../api/axios.js';

type PickerPageResult = {
  items: Array<{ value: string; label: string; code?: string }>;
  page: number;
  pageSize: number;
  totalPages: number;
  isLastPage: boolean;
};

function resolvePagination(
  body: Record<string, unknown>,
  requestedPage: number,
  requestedPageSize: number,
  itemCount: number
) {
  const root = body?.data && typeof body.data === 'object' ? (body.data as Record<string, unknown>) : body;
  const pag = (root?.pagination ?? body?.pagination ?? {}) as Record<string, unknown>;
  const resolvedPage = Number.isFinite(Number(pag.page)) ? Number(pag.page) : requestedPage;
  const totalItems = Number.isFinite(Number(pag.total_items)) ? Number(pag.total_items) : null;
  let totalPages = Number.isFinite(Number(pag.total_pages)) ? Math.max(1, Number(pag.total_pages)) : null;
  if (totalPages == null && totalItems != null && requestedPageSize > 0) {
    totalPages = Math.max(1, Math.ceil(totalItems / requestedPageSize));
  }
  if (totalPages == null) totalPages = 1;
  const isLastPage =
    itemCount < requestedPageSize || (totalPages > 0 && resolvedPage >= totalPages);
  return { page: resolvedPage, totalPages, isLastPage };
}

export async function fetchControlSystemOptionsPage({
  page,
  pageSize = 20,
  search = '',
}: {
  page: number;
  pageSize?: number;
  search?: string;
}): Promise<PickerPageResult> {
  const requestedPageSize = Math.max(1, Number(pageSize) || 20);
  const requestedPage = Math.max(1, Number(page) || 1);

  const response = await axiosInstance.get('/api/systems/options', {
    params: {
      page: requestedPage,
      page_size: requestedPageSize,
      sort_by: 'name',
      sort_order: 'asc',
      ...(search?.trim() ? { search: search.trim() } : {}),
    },
  });

  const body = (response.data ?? {}) as Record<string, unknown>;
  const root = body?.data && typeof body.data === 'object' ? (body.data as Record<string, unknown>) : body;
  const rawItems = Array.isArray(root?.items)
    ? root.items
    : Array.isArray(body?.items)
      ? body.items
      : [];

  const mapped = rawItems
    .map((row: Record<string, unknown>) => {
      const value = String(row?.id ?? '').trim();
      if (!value) return null;
      const systemId = String(row?.systemId ?? row?.system_id ?? '').trim();
      const name = String(row?.name ?? '').trim();
      const labelLead = systemId || name || value;
      const label = name && systemId && name !== systemId ? `${systemId} - ${name}` : labelLead;
      return { value, label };
    })
    .filter(Boolean) as Array<{ value: string; label: string }>;

  const { page: resolvedPage, totalPages, isLastPage } = resolvePagination(
    body,
    requestedPage,
    requestedPageSize,
    mapped.length
  );

  return {
    items: mapped,
    page: resolvedPage,
    pageSize: requestedPageSize,
    totalPages,
    isLastPage,
  };
}

export async function fetchControlItgcDomainOptionsPage({
  page,
  pageSize = 20,
  search = '',
}: {
  page: number;
  pageSize?: number;
  search?: string;
}): Promise<PickerPageResult> {
  const requestedPageSize = Math.max(1, Number(pageSize) || 20);
  const requestedPage = Math.max(1, Number(page) || 1);

  const response = await axiosInstance.get('/api/itgc-domains', {
    params: {
      page: requestedPage,
      page_size: requestedPageSize,
      sort_by: 'code',
      sort_order: 'asc',
      status: 'active',
      ...(search?.trim() ? { search: search.trim() } : {}),
    },
  });

  const body = (response.data ?? {}) as Record<string, unknown>;
  const root = body?.data && typeof body.data === 'object' ? (body.data as Record<string, unknown>) : body;
  const rawItems = Array.isArray(root?.domains)
    ? root.domains
    : Array.isArray(body?.domains)
      ? body.domains
      : [];

  const mapped = rawItems
    .map((row: Record<string, unknown>) => {
      const value = String(row?.id ?? '').trim();
      if (!value) return null;
      const code = String(row?.code ?? '').trim();
      const name = String(row?.name ?? '').trim();
      const label = code && name ? `${code} - ${name}` : name || code || value;
      return { value, label, code: code || undefined };
    })
    .filter(Boolean) as Array<{ value: string; label: string; code?: string }>;

  const { page: resolvedPage, totalPages, isLastPage } = resolvePagination(
    body,
    requestedPage,
    requestedPageSize,
    mapped.length
  );

  return {
    items: mapped,
    page: resolvedPage,
    pageSize: requestedPageSize,
    totalPages,
    isLastPage,
  };
}

export type CreateItgcDomainResult = {
  id: string;
  code: string;
  name: string;
  isSeeded: boolean;
  isActive: boolean;
};

export async function createItgcDomain({
  code,
  name,
}: {
  code: string;
  name: string;
}): Promise<CreateItgcDomainResult> {
  const response = await axiosInstance.post('/api/itgc-domains', {
    code: String(code ?? '').trim(),
    name: String(name ?? '').trim(),
  });
  const body = (response.data ?? {}) as Record<string, unknown>;
  const id = String(body?.id ?? '').trim();
  if (!id) {
    throw new Error('Failed to create domain');
  }
  return {
    id,
    code: String(body?.code ?? '').trim(),
    name: String(body?.name ?? '').trim(),
    isSeeded: Boolean(body?.is_seeded ?? body?.isSeeded),
    isActive: body?.is_active !== false && body?.isActive !== false,
  };
}

export async function fetchControlItgcRiskOptionsPage({
  page,
  pageSize = 20,
  search = '',
  domainId = '',
}: {
  page: number;
  pageSize?: number;
  search?: string;
  domainId?: string;
}): Promise<PickerPageResult> {
  const requestedPageSize = Math.max(1, Number(pageSize) || 20);
  const requestedPage = Math.max(1, Number(page) || 1);
  const trimmedDomainId = String(domainId ?? '').trim();

  if (!trimmedDomainId) {
    return {
      items: [],
      page: requestedPage,
      pageSize: requestedPageSize,
      totalPages: 1,
      isLastPage: true,
    };
  }

  const response = await axiosInstance.get('/api/risks/itgc-options', {
    params: {
      page: requestedPage,
      page_size: requestedPageSize,
      domain_id: trimmedDomainId,
      ...(search?.trim() ? { search: search.trim() } : {}),
    },
  });

  const body = (response.data ?? {}) as Record<string, unknown>;
  const root = body?.data && typeof body.data === 'object' ? (body.data as Record<string, unknown>) : body;
  const rawItems = Array.isArray(root?.items)
    ? root.items
    : Array.isArray(body?.items)
      ? body.items
      : [];

  const mapped = rawItems
    .map((row: Record<string, unknown>) => {
      const value = String(row?.id ?? '').trim();
      if (!value) return null;
      const riskId = String(row?.risk_id ?? '').trim();
      const clientRiskId = String(row?.client_risk_id ?? '').trim();
      const description = String(row?.risk_description ?? '').trim();
      const labelLead = clientRiskId || riskId || value;
      const label = description ? `${labelLead} - ${description}` : labelLead;
      return { value, label };
    })
    .filter(Boolean) as Array<{ value: string; label: string }>;

  const { page: resolvedPage, totalPages, isLastPage } = resolvePagination(
    body,
    requestedPage,
    requestedPageSize,
    mapped.length
  );

  return {
    items: mapped,
    page: resolvedPage,
    pageSize: requestedPageSize,
    totalPages,
    isLastPage,
  };
}
